/**
* LLM 服务 — DeepSeek-coder API 真实集成
*
* demo-gdd §0 锁定：用户自带 key 存 localStorage
* demo-gdd §3.4 Q1 锁定：model = "deepseek-coder"
* v0.6.0 §5.1 + 支柱 5：不靠兜底要可靠 —— 失败显式抛错，不 silent retry
*
* 失败处理原则（v0.6.0 §5.1）：
*   ❌ 不做：AI 失败自动套模板 / 返回上次成功版本 / 静默重试
*   ✅ 做：明确报错（HTTP 状态 + errMsg + 变量名）→ UI 显示给玩家 → 玩家决策
*
 * 真机反馈 #5 改造（2026-07-05）：SYSTEM_PROMPT 硬性规则 6 从"只加 4 行能力说明"扩展为
 *   "4 行能力说明 + 函数体每代码块注释 + 关键 self 调用注释"。理由：玩家在 Monaco 看生成的
 *   Python 代码，只看能力说明看不懂函数体每行在做什么。需要注释告诉他"这段在做什么决策"和
 *   "为什么这个 self.xxx 调用"。不限制代码长度,让玩家自由发挥。
 *   max_tokens 提高到 4096(不限代码长度后需要更大输出空间)。
 *
 * 真机反馈 #8 改造（2026-07-05）：新增 options.currentCode + options.intent，让 LLM 知道
 *   玩家当前编辑器里有什么代码。3 种操作模式:
 *   - create:   无 currentCode,生成全新 bot
 *   - modify:   有 currentCode,基于现有代码最小改动（默认,保护玩家已有工作）
 *   - overwrite:有 currentCode,玩家明确要重写,完全替换
 *   客户端先做 intent 关键词安全网(重写/改成/调整),LLM 再用 SYSTEM_PROMPT 规则 7 二次确认。
 *   关键:modify 模式必须保留/更新 5a 能力说明 + 5b/5c 函数体注释,不能让玩家丢注释。
 */

const DEEPSEEK_API_URL = "https://api.deepseek.com/v1/chat/completions";
const DEEPSEEK_MODEL = "deepseek-coder";
const LLM_KEY_STORAGE = "battlecode-llm-api-key";
const LLM_DAILY_COUNT_STORAGE = "battlecode-llm-daily-count";
const LLM_DAILY_LIMIT = 100;

// demo 阶段预置的 DeepSeek API Key（用户授权 demo 使用，避免每次手动输入）
// 玩家在【设置】页可手动覆盖；如 localStorage 已有用户自定义 key，优先使用
const HARDCODED_DEMO_KEY = "sk-0e72404214204a839e095e9cc7f8655a";

// SYSTEM_PROMPT 外提为独立 .md 文件（Harness Engineering 约束层）
// 版本管理 + diff + 多人维护。Vite 构建时静态内联，零运行时 IO。
import SYSTEM_PROMPT from "./llm-system-prompt.md?raw";

// V5: 分阶段 SYSTEM_PROMPT（阶段 0-4）
import SYSTEM_PROMPT_P0 from "./llm-system-prompt-p0.md?raw";
import SYSTEM_PROMPT_P1 from "./llm-system-prompt-p1.md?raw";
import SYSTEM_PROMPT_P2 from "./llm-system-prompt-p2.md?raw";
import SYSTEM_PROMPT_P3 from "./llm-system-prompt-p3.md?raw";
import SYSTEM_PROMPT_P4 from "./llm-system-prompt-p4.md?raw";

import type { Phase } from "./api-surface-manager";

/** 根据教学阶段选择合适的 SYSTEM_PROMPT */
export function getSystemPromptForPhase(phase?: Phase): string {
  if (phase === undefined) return SYSTEM_PROMPT; // 非教学关卡用全量 prompt
  const prompts: Record<number, string> = {
    0: SYSTEM_PROMPT_P0,
    1: SYSTEM_PROMPT_P1,
    2: SYSTEM_PROMPT_P2,
    3: SYSTEM_PROMPT_P3,
    4: SYSTEM_PROMPT_P4,
  };
  return prompts[phase] ?? SYSTEM_PROMPT;
}

export interface LLMError {
  ok: false;
  stage: "no_key" | "quota" | "network" | "http" | "parse" | "empty";
  message: string;
  httpStatus?: number;
  rawBody?: string;
}

/** 真机反馈 #8:生成选项 — 让 LLM 知道玩家当前编辑器里的代码,以及客户端推断的意图 */
export interface GenerateOptions {
  /** 玩家当前编辑器里的代码。可空(空 = create 模式,首次创建) */
  currentCode?: string;
  /** 客户端基于 prompt 关键词推断的意图(LLM 会用规则 7 二次确认)。V14 新增 "ask" */
  intent?: "ask" | "modify" | "overwrite";
  /** V5: 教学阶段（0-4），决定使用哪个分阶段 SYSTEM_PROMPT */
  phase?: Phase;
  /** V14: 多轮对话历史（不含当前消息），传给 LLM 以感知上下文 */
  history?: { role: string; content: string }[];
}

// ============================================================
// V3-005(2026-07-13): from_strategy 模式 — 从战略文档生成多坦克代码
// ============================================================

export interface FromStrategyRequest {
  mode: "from_strategy";
  /** 完整战略文档（markdown） */
  strategy_doc: string;
  /** 当前要生成代码的坦克索引（0-based） */
  tank_index: number;
  /** 总坦克数 */
  total_tanks: number;
  /** 同队已生成坦克的代码摘要（tank_index < current，用于协同参考） */
  ally_context?: string[];
  /** 地图尺寸（用于 AI 感知战场大小） */
  map_size: [number, number];
}

/** 生成进度回调类型 */
export type FromStrategyProgressCallback = (
  tankIndex: number,
  totalTanks: number,
  phase: "generating" | "done" | "error",
  errorMessage?: string,
) => void;

export interface BattleAnalysisOptions {
  projectId: string;
  projectName: string;
  summary: {
    winner: "team_a" | "team_b" | "draw";
    reason: string;
    totalTicks: number;
    playerKills: number;
    playerDeaths: number;
    durationMs: number;
    playerShotsFired?: number;
    playerFinalHp?: number;
    enemyFinalHp?: number;
    playerActions?: { move: number; shoot: number; hit: number; damage: number };
    aiActions?: { move: number; shoot: number; hit: number; damage: number };
    keyEvents?: Array<{ eventType: string; tickNum: number; actor: "team_a" | "team_b"; description: string }>;
    consoleLog?: Array<{ tick: number; tag: string; team: string; message: string; fileLine?: string }>;
    replay?: Array<{ tick: number; state: unknown; playerAction: unknown; aiAction: unknown }>;
  };
  tacticalDimensions: Array<{ key: string; label: string; score: number; verdict: string; evidence: string }>;
  keyEvents: Array<{ id: string; tick: number; side: string; label: string; description: string }>;
  logs: Array<{ tick: number; tag: string; team: string; message: string; fileLine?: string; filter?: string }>;
  question?: string;
}

export interface BattleAnalysisResult {
  overview: string;
  keyPointText: string;
  nextStep: string;
}

export interface LLMSuccess {
  ok: true;
  code: string;
  prompt: string;
  usage?: { prompt_tokens: number; completion_tokens: number; total_tokens: number };
}

export type LLMResult = LLMError | LLMSuccess;

/** 读取用户 API key（优先 localStorage 用户自定义值，否则用 demo 预置 key） */
export function getLLMApiKey(): string {
  return localStorage.getItem(LLM_KEY_STORAGE) || HARDCODED_DEMO_KEY;
}

/** demo 阶段启动时把预置 key 写进 localStorage（仅当用户从未设置过） */
export function ensureDemoKeyPersisted(): void {
  if (!localStorage.getItem(LLM_KEY_STORAGE)) {
    localStorage.setItem(LLM_KEY_STORAGE, HARDCODED_DEMO_KEY);
  }
}

/** 今日用量计数（防 demo 阶段超 100 req/day/IP 限制） */
function bumpDailyCount(): { count: number; limit: number; exceeded: boolean } {
  const today = new Date().toISOString().slice(0, 10);
  const key = `${LLM_DAILY_COUNT_STORAGE}-${today}`;
  const count = parseInt(localStorage.getItem(key) ?? "0", 10) + 1;
  localStorage.setItem(key, String(count));
  return { count, limit: LLM_DAILY_LIMIT, exceeded: count > LLM_DAILY_LIMIT };
}

// ===== V11 部署模式检测 =====

const AUTH_TOKEN_STORAGE = "battlecode-auth-token";

/** 是否生产部署环境（非 localhost） */
export function isProductionDeploy(): boolean {
  const host = window.location.hostname;
  return host !== "localhost" && host !== "127.0.0.1";
}

/** 获取 JWT 鉴权 token */
export function getAuthToken(): string | null {
  return localStorage.getItem(AUTH_TOKEN_STORAGE);
}

/** 保存 JWT token */
export function setAuthToken(token: string): void {
  localStorage.setItem(AUTH_TOKEN_STORAGE, token);
}

/** 清除 JWT token（退出登录） */
export function clearAuthToken(): void {
  localStorage.removeItem(AUTH_TOKEN_STORAGE);
}

// ===== V11 用户资料 =====

const USER_PROFILE_STORAGE = "battlecode-user-profile";

export interface UserProfile {
  openid: string;
  provider: string;
  name: string;
  avatar: string;
}

interface JwtClaims {
  openid?: string;
  provider?: string;
  name?: string;
  avatar?: string;
  exp?: number;
}

function decodeJwtClaims(token: string): JwtClaims {
  const payload = token.split(".")[1];
  if (!payload) throw new Error("登录令牌格式无效");
  try {
    const normalized = payload.replace(/-/g, "+").replace(/_/g, "/").padEnd(Math.ceil(payload.length / 4) * 4, "=");
    return JSON.parse(atob(normalized)) as JwtClaims;
  } catch {
    throw new Error("登录令牌无法解析");
  }
}

/** 原子建立账号会话：先冲刷旧账号，再写入新 token，最后 hydrate 新账号。 */
export async function setAuthSession(token: string, profile: UserProfile): Promise<void> {
  const claims = decodeJwtClaims(token);
  if (!claims.openid || claims.openid !== profile.openid) {
    throw new Error("登录身份校验失败");
  }
  if (claims.exp && claims.exp * 1000 <= Date.now()) throw new Error("登录令牌已过期");

  const { setCurrentUser, notifyUserReady } = await import("../core/user-context.js");
  await setCurrentUser({ openid: claims.openid, name: claims.name ?? profile.name }, { deferChange: true });
  setAuthToken(token);
  const normalizedProfile = {
    ...profile,
    openid: claims.openid,
    provider: claims.provider ?? profile.provider,
    name: claims.name ?? profile.name,
    avatar: claims.avatar ?? profile.avatar,
  };
  localStorage.setItem(USER_PROFILE_STORAGE, JSON.stringify(normalizedProfile));
  await notifyUserReady();
}

/** 兼容旧调用方；新登录流程必须使用 setAuthSession。 */
export async function setUserProfile(profile: UserProfile): Promise<void> {
  const token = getAuthToken();
  if (!token) throw new Error("缺少登录令牌");
  await setAuthSession(token, profile);
}

/** 获取用户资料 */
export function getUserProfile(): UserProfile | null {
  const raw = localStorage.getItem(USER_PROFILE_STORAGE);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as UserProfile;
  } catch {
    return null;
  }
}

/** 退出登录（清除 token + 资料 + V12 用户上下文） */
export async function clearAuth(): Promise<void> {
  // V14：先以旧用户身份完成待同步数据推送，再清除 token/profile。
  const { clearCurrentUser } = await import("../core/user-context.js");
  await clearCurrentUser();
  clearAuthToken();
  localStorage.removeItem(USER_PROFILE_STORAGE);
}

/**
 * V11 代理模式：通过 /api/llm/generate 调用 LLM
 * 服务端持有 API Key，前端不暴露
 */
async function callLLMProxy(
  systemPrompt: string,
  userMessage: string,
  signal?: AbortSignal,
): Promise<{ ok: true; content: string; usage?: object } | { ok: false; error: string }> {
  const token = getAuthToken();
  const headers: Record<string, string> = { "Content-Type": "application/json" };
  if (token) headers["Authorization"] = `Bearer ${token}`;

  try {
    const res = await fetch("/api/llm/generate", {
      method: "POST",
      headers,
      body: JSON.stringify({ systemPrompt, userMessage }),
      signal,
    });

    if (!res.ok) {
      let rawBody = "";
      try { rawBody = await res.text(); } catch { /* ignore */ }
      return { ok: false, error: `LLM 代理错误 HTTP ${res.status}: ${rawBody.slice(0, 200)}` };
    }

    const json = await res.json();
    if (!json.ok) {
      return { ok: false, error: json.message || "LLM 代理返回失败" };
    }
    return { ok: true, content: json.content, usage: json.usage };
  } catch (e) {
    if ((e as Error).name === "AbortError") {
      return { ok: false, error: "请求已取消" };
    }
    return { ok: false, error: `代理请求失败: ${(e as Error).message}` };
  }
}

/**
 * V11 代理模式下生成 bot 代码
 * 通过 /api/llm/generate 调用，服务端持有 API Key
 */
async function generateBotCodeViaProxy(prompt: string, options?: GenerateOptions): Promise<LLMResult> {
  const userMsg = buildUserMessage(prompt, options);
  const systemPrompt = getSystemPromptForPhase(options?.phase);

  console.info(`[LLM-proxy] → /api/llm/generate · userMsg len=${userMsg.length} chars`);

  const result = await callLLMProxy(systemPrompt, userMsg);

  if (!result.ok) {
    console.error(`[LLM-proxy] failed: ${result.error}`);
    // 映射错误类型
    if (result.error.includes("代理错误 HTTP 503")) {
      return { ok: false, stage: "no_key", message: "服务器 LLM Key 未配置，请联系管理员。" };
    }
    if (result.error.includes("代理错误 HTTP 401")) {
      return { ok: false, stage: "network", message: "登录已过期，请重新扫码登录。" };
    }
    return { ok: false, stage: "network", message: result.error };
  }

  console.info(`[LLM-proxy] ← OK · raw len=${result.content.length} chars`);

  // 提取代码
  const code = extractCode(result.content);
  if (!code) {
    console.error("[LLM-proxy] parse error: extractCode returned empty");
    return { ok: false, stage: "parse", message: "LLM 返回了内容但未包含有效 Python 代码块。请尝试更具体的描述。" };
  }

  return {
    ok: true,
    code,
    prompt: result.content,
    usage: result.usage as LLMSuccess["usage"],
  };
}

/** 从 LLM 响应中提取 Python 代码块（兼容 ```python ... ``` 包裹） */
function extractCode(raw: string): string {
  if (!raw) return "";
  // 优先匹配 ```python ... ```
  const fenced = raw.match(/```(?:python|py)?\s*\n([\s\S]*?)```/i);
  if (fenced) return fenced[1].trim();
  // 退而求其次：如果整段就以 def 开头，认为是裸代码
  if (/^\s*def\s+\w+\s*\(/.test(raw)) return raw.trim();
  return raw.trim();
}

/**
 * 真机反馈 #8:把 options 拼成结构化 user message
 *
 * 三种模式:
 *  - create:    无 currentCode(空项目首次生成 / 测试连通性)
 *  - modify:    有 currentCode,客户端关键词判断为"小改动"(默认,更安全)
 *  - overwrite: 有 currentCode,客户端关键词判断为"完全重写"
 *
 * 客户端关键词可能误判,所以在 [操作模式] 行明确告诉 LLM:
 *  "你看到 modify 标记,但如果玩家指令明显是重写,按 overwrite 处理"
 *  详见 SYSTEM_PROMPT 硬性规则 7
 */
function buildUserMessage(prompt: string, options?: GenerateOptions): string {
  const code = options?.currentCode?.trim();
  const intent = options?.intent;

  // V14 模式 0:ask — 纯文本沟通，不写代码
  if (intent === "ask") {
    const codeContext = code
      ? [
          "[当前代码(仅作讨论上下文,不需要输出代码)]",
          "```python",
          code,
          "```",
          "",
        ].join("\n")
      : "";
    return [
      codeContext,
      "[玩家问题]",
      prompt,
      "",
      "[操作模式] ask — 纯文本讨论模式。直接回答问题,分析代码优缺点,给出战术建议。不要输出任何 Python 代码。",
    ].filter(Boolean).join("\n");
  }

  // 模式 1:create — 无 currentCode 或 currentCode 为空
  if (!code) {
    return [
      "[玩家指令]",
      prompt,
      "",
      "[操作模式] create — 没有现有代码,请生成全新 bot(能力说明 + 函数体注释按硬性规则 6 全配齐)。",
    ].join("\n");
  }

  // 模式 2:modify — 有 currentCode + intent=modify(默认,保护玩家已有工作)
  if (intent === "modify") {
    return [
      "[当前代码(基于它做最小化修改,只动需要动的部分)]",
      "```python",
      code,
      "```",
      "",
      "[玩家指令]",
      prompt,
      "",
      "[操作模式] modify — 基于上面'当前代码'做最小化改动。必须保留/更新 5a 4 行能力说明(战术/使用 API/核心策略要同步反映新逻辑),5b/5c 函数体注释也要保留并同步更新。",
      "[注意] 如果玩家指令明显是'重写/重做/换新/覆盖/重新来/从头来'语义,即使 [操作模式] 标 modify,你也应该按 overwrite 处理(完全重写)。最终判断以玩家指令文本为准。",
    ].join("\n");
  }

  // 模式 3:overwrite — 有 currentCode + intent=overwrite
  return [
    "[当前代码(本次将完全忽略,仅作上下文参考)]",
    "```python",
    code,
    "```",
    "",
    "[玩家指令]",
    prompt,
    "",
    "[操作模式] overwrite — 完全重写,忽略上面'当前代码',按玩家指令生成全新代码(注释密度同 create 模式,5a/5b/5c 全配齐)。",
  ].join("\n");
}

/**
 * 调用 DeepSeek-coder 生成 bot 代码
 *
 * @param prompt 玩家自然语言描述（Vibe 弹窗 textarea）
 * @param options 真机反馈 #8:currentCode + intent,让 LLM 知道玩家当前代码(详见 buildUserMessage)
 * @returns 成功返回 code，失败返回带 stage/message 的错误对象（UI 显式展示）
 */
export async function generateBotCode(prompt: string, options?: GenerateOptions): Promise<LLMResult> {
  const intentTag = options?.intent
    ? options.intent
    : (options?.currentCode ? "modify" : "create");
  console.info(`[LLM] generateBotCode called · prompt len=${prompt.length} chars · intent=${intentTag}`);

  // V11 代理模式：生产环境走 /api/llm/generate
  if (isProductionDeploy()) {
    return generateBotCodeViaProxy(prompt, options);
  }

  // 1. 检查 key
  const apiKey = getLLMApiKey();
  console.info(`[LLM] generateBotCode called · prompt len=${prompt.length} chars`);
  console.info(`[LLM] apiKey: ${apiKey ? `${apiKey.slice(0, 7)}…${apiKey.slice(-4)} (${apiKey.length} chars, source=${localStorage.getItem(LLM_KEY_STORAGE) ? "localStorage" : "demo-hardcoded"})` : "MISSING"}`);
  if (!apiKey) {
    console.error("[LLM] aborted: no API key");
    return {
      ok: false,
      stage: "no_key",
      message: "未配置 API Key。请到【设置 (Settings)】页填入 DeepSeek-coder API Key。",
    };
  }

  // 2. 检查每日 quota
  const daily = bumpDailyCount();
  console.info(`[LLM] daily usage: ${daily.count} / ${daily.limit}`);
  if (daily.exceeded) {
    console.error(`[LLM] aborted: quota exceeded (${daily.count}/${daily.limit})`);
    return {
      ok: false,
      stage: "quota",
      message: `今日用量已超上限（${daily.count} / ${daily.limit} req）。明天再来，或换个 key。`,
    };
  }

  // 3. 构造请求（demo-gdd §3.4 锁 model + max_tokens）
  // 真机反馈 #8:user message 用 buildUserMessage 拼装,把 currentCode + intent 一起发
  const userMsg = buildUserMessage(prompt, options);
  console.info(`[LLM] currentCode len=${options?.currentCode?.length ?? 0} chars · userMsg len=${userMsg.length} chars`);
  const systemPrompt = getSystemPromptForPhase(options?.phase);
  const body = {
    model: DEEPSEEK_MODEL,
    messages: [
      { role: "system", content: systemPrompt },
      { role: "user", content: userMsg },
    ],
    max_tokens: 4096,
    temperature: 0.3,
    stream: false,
  };
  console.info(`[LLM] request body: model=${body.model} max_tokens=${body.max_tokens} messages=${body.messages.length}${options?.phase !== undefined ? ` phase=${options.phase}` : ""}`);

  // 4. 真实 fetch（**不** silent retry，v0.6.0 §5.1 铁律）
  let res: Response;
  try {
    console.info(`[LLM] → POST ${DEEPSEEK_API_URL}`);
    res = await fetch(DEEPSEEK_API_URL, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${apiKey}`,
      },
      body: JSON.stringify(body),
    });
    console.info(`[LLM] ← ${res.status} ${res.statusText} · content-type=${res.headers.get("content-type")}`);
  } catch (e) {
    // 网络层失败（DNS / CORS / 断网）
    console.error("[LLM] network error:", e);
    return {
      ok: false,
      stage: "network",
      message: `网络请求失败：${(e as Error).message}。检查网络后重试，不要靠自动兜底。`,
    };
  }

  // 5. HTTP 错误
  if (!res.ok) {
    let rawBody = "";
    try {
      rawBody = await res.text();
    } catch {
      // ignore
    }
    console.error(`[LLM] HTTP ${res.status}:`, rawBody);
    return {
      ok: false,
      stage: "http",
      httpStatus: res.status,
      rawBody,
      message: `DeepSeek API 报错：HTTP ${res.status} ${res.statusText}。${
        res.status === 401
          ? "（key 无效或过期，请到设置页更新）"
          : res.status === 429
          ? "（限流，明日再试）"
          : res.status === 402
          ? "（账户欠费，去 DeepSeek 平台充值）"
          : "（查看 CONSOLE 完整 rawBody 排查）"
      }`,
    };
  }

  // 6. 解析 JSON
  let json: any;
  try {
    json = await res.json();
  } catch (e) {
    console.error("[LLM] parse error:", e);
    return {
      ok: false,
      stage: "parse",
      message: `DeepSeek 返回非 JSON：${(e as Error).message}`,
    };
  }

  // 7. 提取 content
  const content: string = json?.choices?.[0]?.message?.content ?? "";
  const code = extractCode(content);
  console.info(`[LLM] parsed: content=${content.length} chars, code=${code.length} chars, usage=${JSON.stringify(json.usage ?? null)}`);
  if (!code) {
    console.error("[LLM] empty code extracted from content:", content);
    return {
      ok: false,
      stage: "empty",
      message: "LLM 返回内容为空或无法识别为 Python 代码。修改 prompt 重试。",
    };
  }

  console.info(`[LLM] ✓ success · returning ${code.length} chars code`);
  return {
    ok: true,
    code,
    prompt,
    usage: json.usage,
  };
}

/** 测试连通性（设置页 "测试" 按钮可调） */
export async function testLLMConnection(): Promise<LLMResult> {
  return generateBotCode("# ping\nprint only a comment, no logic");
}

/**
 * P1-#7:GDD §3.4 锁定 stream: true 流式输出
 * 边接收边 yield code 片段,调用方负责实时写入 Monaco
 * @param prompt 玩家自然语言描述
 * @param onChunk 每个 delta 文本片段到达时回调(逐 token 显示给玩家)
 * @returns 完整代码 + usage
 */
export interface StreamHandle {
  code: string;        // 累积完整代码
  chunks: number;      // 已接收 chunk 数
  cancelled: boolean;  // 调用方 AbortSignal 触发后置 true
}
export async function generateBotCodeStream(
  prompt: string,
  onChunk: (delta: string, accumulated: string) => void,
  signal?: AbortSignal,
  options?: GenerateOptions,
): Promise<LLMResult> {
  // V11 代理模式：不支持 SSE 流式，回退到非流式 generateBotCode
  if (isProductionDeploy()) {
    console.info("[LLM stream] proxy mode: falling back to non-stream generateBotCode");
    return generateBotCode(prompt, options);
  }

  // 1. 检查 key
  const apiKey = getLLMApiKey();
  if (!apiKey) {
    return { ok: false, stage: "no_key", message: "未配置 API Key。" };
  }
  // 2. quota
  const daily = bumpDailyCount();
  if (daily.exceeded) {
    return { ok: false, stage: "quota", message: `今日用量超限（${daily.count}/${daily.limit}）。` };
  }

  // 3. 构造 stream 请求
  // V14: history 支持多轮对话上下文
  // 真机反馈 #8:user message 用 buildUserMessage 拼装,把 currentCode + intent 一起发
  const userMsg = buildUserMessage(prompt, options);
  const intentTag = options?.intent
    ? options.intent
    : (options?.currentCode ? (options.intent === "overwrite" ? "overwrite" : "modify") : "create");
  console.info(`[LLM stream] intent=${intentTag} · currentCode len=${options?.currentCode?.length ?? 0} chars · userMsg len=${userMsg.length} chars · history=${options?.history?.length ?? 0} rounds`);
  const systemPrompt = getSystemPromptForPhase(options?.phase);
  const messages: { role: string; content: string }[] = [
    { role: "system", content: systemPrompt },
  ];

  // V14: 注入多轮对话历史（system 之后、当前消息之前）
  if (options?.history && options.history.length > 0) {
    for (const entry of options.history) {
      messages.push({ role: entry.role, content: entry.content });
    }
  }

  messages.push({ role: "user", content: userMsg });

  const body = {
    model: DEEPSEEK_MODEL,
    messages,
    max_tokens: 4096,
    temperature: 0.3,
    stream: true,
  };

  // 4. fetch
  let res: Response;
  try {
    res = await fetch(DEEPSEEK_API_URL, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${apiKey}`,
      },
      body: JSON.stringify(body),
      signal,
    });
  } catch (e) {
    return { ok: false, stage: "network", message: `网络请求失败：${(e as Error).message}` };
  }
  if (!res.ok) {
    let rawBody = "";
    try { rawBody = await res.text(); } catch { /* ignore */ }
    return { ok: false, stage: "http", httpStatus: res.status, rawBody, message: `DeepSeek API 报错：HTTP ${res.status}` };
  }
  if (!res.body) {
    return { ok: false, stage: "parse", message: "DeepSeek 流式响应无 body。" };
  }

  // 5. 解析 SSE (Server-Sent Events)
  // DeepSeek 格式:`data: {"choices":[{"delta":{"content":"..."}}]}\n\n`
  // 流结束时:`data: [DONE]\n\n`
  const reader = res.body.getReader();
  const decoder = new TextDecoder("utf-8");
  let buffer = "";
  let accumulatedRaw = "";
  let chunks = 0;

  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      if (signal?.aborted) {
        reader.cancel();
        return { ok: false, stage: "parse", message: "已取消流式生成" };
      }
      buffer += decoder.decode(value, { stream: true });
      // 按 \n\n 分块
      const events = buffer.split("\n\n");
      buffer = events.pop() || "";
      for (const evt of events) {
        const line = evt.trim();
        if (!line.startsWith("data:")) continue;
        const payload = line.slice(5).trim();
        if (payload === "[DONE]") continue;
        try {
          const parsed = JSON.parse(payload);
          const delta: string = parsed?.choices?.[0]?.delta?.content ?? "";
          if (delta) {
            accumulatedRaw += delta;
            chunks++;
            onChunk(delta, accumulatedRaw);
          }
        } catch (e) {
          // 部分 chunk 可能 JSON 不完整,跳过
          console.warn("[LLM stream] partial JSON skipped:", payload.slice(0, 80));
        }
      }
    }
  } catch (e) {
    return { ok: false, stage: "network", message: `流式读取失败：${(e as Error).message}` };
  }

  // 6. 提取 code
  const code = extractCode(accumulatedRaw);
  if (!code) {
    return { ok: false, stage: "empty", message: "LLM 流式返回内容为空。" };
  }
  console.info(`[LLM stream] ✓ success · ${chunks} chunks, ${code.length} chars code`);
  return { ok: true, code, prompt };
}

// ============================================================
// V3-005(2026-07-13): from_strategy 模式 — 从战略文档生成代码
// ============================================================

/**
 * 拼接 from_strategy 模式的 user message。
 */
function buildFromStrategyUserMessage(req: FromStrategyRequest): string {
  const allySection = req.ally_context && req.ally_context.length > 0
    ? [
        "[同队坦克代码（已生成，供协同参考）]",
        ...req.ally_context.map((summary, i) => `坦克 ${i} 代码摘要：\n${summary}`),
        "",
      ].join("\n")
    : "";

  return [
    "[操作模式] from_strategy — 从战略文档生成坦克代码",
    "",
    `[坦克索引] tank_index = ${req.tank_index} / ${req.total_tanks}（为第 ${req.tank_index} 个坦克生成 think() 函数）`,
    `[地图尺寸] ${req.map_size[0]} × ${req.map_size[1]}`,
    "",
    "[战略文档]",
    "```markdown",
    req.strategy_doc,
    "```",
    "",
    allySection,
    "请分析以上战略文档，为 tank_index = " + req.tank_index + " 生成完整的 Python think(self) 函数。",
    "要求：",
    "1. 必须体现该坦克的角色分工（战术定位 + 攻击偏好 + 移动模式）",
    "2. 如果角色卡有特殊行为触发条件，必须实现对应逻辑",
    allySection
    ? "3. 考虑与已生成队友的协同（协同规则 + 协同行为）"
    : "3. 按角色卡独立设计战术逻辑（后续坦克会参考本坦克代码）",
    "4. 保留完整的三段注释（5a 能力说明 + 5b 块注释 + 5c 关键调用注释）",
    "5. 使用 self.tank_index 区分角色身份",
    "6. 仅输出 def think(self): 函数定义，不要额外解释",
  ].join("\n");
}

/**
 * V3-005:从战略文档生成单辆坦克的代码（非流式，串行调用）。
 *
 * @param req  from_strategy 请求参数
 * @returns  成功返回 code，失败返回带 stage/message 的 LLMError
 */
export async function generateFromStrategy(req: FromStrategyRequest): Promise<LLMResult> {
  const apiKey = getLLMApiKey();
  if (!apiKey) {
    return { ok: false, stage: "no_key", message: "未配置 API Key。" };
  }

  const daily = bumpDailyCount();
  if (daily.exceeded) {
    return { ok: false, stage: "quota", message: `今日用量超限（${daily.count}/${daily.limit}）。` };
  }

  const userMsg = buildFromStrategyUserMessage(req);
  console.info(`[LLM from_strategy] tank_index=${req.tank_index}/${req.total_tanks} · userMsg len=${userMsg.length} chars`);

  // from_strategy 使用全量 SYSTEM_PROMPT（多坦克模式不需要分阶段）
  const body = {
    model: DEEPSEEK_MODEL,
    messages: [
      { role: "system", content: SYSTEM_PROMPT },
      { role: "user", content: userMsg },
    ],
    max_tokens: 4096,
    temperature: 0.3,
    stream: false,
  };

  let res: Response;
  try {
    res = await fetch(DEEPSEEK_API_URL, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${apiKey}`,
      },
      body: JSON.stringify(body),
    });
  } catch (e) {
    return { ok: false, stage: "network", message: `网络请求失败：${(e as Error).message}` };
  }

  if (!res.ok) {
    let rawBody = "";
    try { rawBody = await res.text(); } catch { /* ignore */ }
    return { ok: false, stage: "http", httpStatus: res.status, rawBody, message: `HTTP ${res.status}` };
  }

  let json: any;
  try {
    json = await res.json();
  } catch (e) {
    return { ok: false, stage: "parse", message: `JSON 解析失败: ${(e as Error).message}` };
  }

  const content: string = json?.choices?.[0]?.message?.content ?? "";
  const code = extractCode(content);
  if (!code) {
    return { ok: false, stage: "empty", message: "LLM 返回空代码。修改作战计划后重试。" };
  }

  console.info(`[LLM from_strategy] tank_${req.tank_index} ✓ · ${code.length} chars code`);
  return { ok: true, code, prompt: req.strategy_doc, usage: json.usage };
}

/**
 * V3-005:串行生成所有坦克代码（from_strategy × N）。
 * 每生成一个坦克后回调 onProgress，UI 可据此更新进度条。
 *
 * @param strategyDoc     完整战略文档（markdown）
 * @param totalTanks      总坦克数
 * @param mapSize         地图尺寸
 * @param onProgress      进度回调（可选，用于 UI 进度条）
 * @returns 成功返回 codes[]，失败返回第一个 LLMError
 */
export async function generateAllFromStrategy(
  strategyDoc: string,
  totalTanks: number,
  mapSize: [number, number],
  onProgress?: FromStrategyProgressCallback,
): Promise<{ ok: true; codes: string[] } | LLMError> {
  const codes: string[] = [];
  const allySummaries: string[] = [];

  for (let i = 0; i < totalTanks; i++) {
    onProgress?.(i, totalTanks, "generating");

    const result = await generateFromStrategy({
      mode: "from_strategy",
      strategy_doc: strategyDoc,
      tank_index: i,
      total_tanks: totalTanks,
      ally_context: allySummaries.length > 0 ? allySummaries : undefined,
      map_size: mapSize,
    });

    if (!result.ok) {
      onProgress?.(i, totalTanks, "error", result.message);
      return result; // 返回第一个 LLMError，调用方决定是否继续
    }

    codes.push(result.code);
    // 摘要：取代码前 200 字符 + 能力说明（如果存在）
    const summary = extractCodeSummary(result.code);
    allySummaries.push(summary);
    onProgress?.(i, totalTanks, "done");
  }

  return { ok: true, codes };
}

/** 从生成的代码中提取简短摘要（前 200 字符 + 能力说明行） */
function extractCodeSummary(code: string): string {
  const lines = code.split("\n");
  const abilityLines: string[] = [];
  let inAbility = false;
  for (const line of lines) {
    if (line.includes("=== Bot 能力说明 ===")) { inAbility = true; continue; }
    if (line.includes("=== /Bot 能力说明 ===")) break;
    if (inAbility) abilityLines.push(line.trim());
  }

  const firstLines = lines.filter((l) => l.trim() && !l.trim().startsWith("#")).slice(0, 3);
  const summary = [
    ...(abilityLines.length > 0 ? abilityLines : []),
    ...firstLines.map((l) => l.trim().slice(0, 80)),
  ].join(" | ");

  return summary.slice(0, 200);
}

// ============================================================
// V3-005 可视化阅读 — visualize_strategy（Markdown → 战术图描述）
// ============================================================

/**
 * 从战略文档生成战术可视化描述（流程图节点+连线）。
 * 一次性产物，不持久化。LLM 返回结构化描述，前端解析渲染。
 *
 * @param strategyDoc  完整战略文档（markdown）
 * @param totalTanks   编队坦克数
 * @returns 成功返回可视化描述 JSON，失败返回 LLMError
 */
export async function generateVisualizeStrategy(
  strategyDoc: string,
  totalTanks: number,
): Promise<LLMResult> {
  const apiKey = getLLMApiKey();
  if (!apiKey) {
    return { ok: false, stage: "no_key", message: "未配置 API Key。" };
  }

  const daily = bumpDailyCount();
  if (daily.exceeded) {
    return { ok: false, stage: "quota", message: `今日用量超限(${daily.count}/${daily.limit})。` };
  }

  const userMsg = [
    "[操作模式] visualize_strategy",
    `[编队坦克数] ${totalTanks}`,
    "",
    "[战略文档]",
    "```markdown",
    strategyDoc,
    "```",
    "",
    "请分析以上战略文档，生成战术可视化描述（Mermaid flowchart 格式）。",
  ].join("\n");

  console.info(`[LLM visualize] tanks=${totalTanks} · doc len=${strategyDoc.length} chars`);

  const body = {
    model: DEEPSEEK_MODEL,
    messages: [
      { role: "system", content: SYSTEM_PROMPT },
      { role: "user", content: userMsg },
    ],
    max_tokens: 1500,
    temperature: 0.2,
    stream: false,
  };

  let res: Response;
  try {
    res = await fetch(DEEPSEEK_API_URL, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${apiKey}`,
      },
      body: JSON.stringify(body),
    });
  } catch (e) {
    return { ok: false, stage: "network", message: `网络请求失败：${(e as Error).message}` };
  }

  if (!res.ok) {
    let rawBody = "";
    try { rawBody = await res.text(); } catch { /* ignore */ }
    return { ok: false, stage: "http", httpStatus: res.status, rawBody, message: `HTTP ${res.status}` };
  }

  let json: any;
  try {
    json = await res.json();
  } catch (e) {
    return { ok: false, stage: "parse", message: `JSON 解析失败: ${(e as Error).message}` };
  }

  const raw = json?.choices?.[0]?.message?.content as string | undefined;
  if (!raw) {
    return { ok: false, stage: "empty", message: "LLM 返回空响应。" };
  }

  // 提取 mermaid 代码块内容
  let extracted = raw;
  const mermaidMatch = raw.match(/```(?:mermaid)?\s*\n([\s\S]*?)```/);
  if (mermaidMatch) {
    extracted = mermaidMatch[1].trim();
  }

  return { ok: true, code: extracted, prompt: userMsg, usage: json.usage };
}

// ============================================================
// V3-005 Phase 3: expand_strategy — 快速部署（自然语言 → 作战计划）
// ============================================================

/**
 * 将用户的自然语言战术描述展开为结构化作战计划（markdown）。
 * 单次 LLM 调用，不生成 Python 代码，只输出作战计划。
 *
 * @param prompt      用户的自然语言战术描述
 * @param totalTanks  编队坦克数 N（2 或 3）
 * @returns 成功返回 markdown，失败返回 LLMError
 */
export async function generateExpandStrategy(
  prompt: string,
  totalTanks: number,
): Promise<LLMResult> {
  const apiKey = getLLMApiKey();
  if (!apiKey) {
    return { ok: false, stage: "no_key", message: "未配置 API Key。" };
  }

  const daily = bumpDailyCount();
  if (daily.exceeded) {
    return { ok: false, stage: "quota", message: `今日用量超限（${daily.count}/${daily.limit}）。` };
  }

  const userMsg = [
    `[操作模式] expand_strategy`,
    `[编队坦克数] ${totalTanks}`,
    ``,
    `[战术描述]`,
    prompt,
    ``,
    `请将以上战术描述展开为结构化作战计划。`,
  ].join("\n");

  console.info(`[LLM expand_strategy] tanks=${totalTanks} · prompt len=${prompt.length} chars`);

  const body = {
    model: DEEPSEEK_MODEL,
    messages: [
      { role: "system", content: SYSTEM_PROMPT },
      { role: "user", content: userMsg },
    ],
    max_tokens: 2048,
    temperature: 0.7,
    stream: false,
  };

  let res: Response;
  try {
    res = await fetch(DEEPSEEK_API_URL, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${apiKey}`,
      },
      body: JSON.stringify(body),
    });
  } catch (e) {
    return { ok: false, stage: "network", message: `网络请求失败：${(e as Error).message}` };
  }

  if (!res.ok) {
    let rawBody = "";
    try { rawBody = await res.text(); } catch { /* ignore */ }
    return { ok: false, stage: "http", httpStatus: res.status, rawBody, message: `HTTP ${res.status}` };
  }

  let json: any;
  try {
    json = await res.json();
  } catch (e) {
    return { ok: false, stage: "parse", message: `JSON 解析失败: ${(e as Error).message}` };
  }

  const raw = json?.choices?.[0]?.message?.content as string | undefined;
  if (!raw) {
    return { ok: false, stage: "empty", message: "LLM 返回了空响应。" };
  }

  // 尝试提取 markdown 代码块内的内容，或直接使用 raw
  let extracted = raw;
  const mdBlockMatch = raw.match(/```(?:markdown)?\s*\n([\s\S]*?)```/);
  if (mdBlockMatch) {
    extracted = mdBlockMatch[1].trim();
  } else {
    // 去掉 LLM 可能附加的解释文字，只取 # 开头的内容
    const headingIdx = extracted.indexOf("# 编队战术");
    if (headingIdx > 0) {
      extracted = extracted.slice(headingIdx);
    }
  }

  return { ok: true, code: extracted, prompt: userMsg };
}

/**
 * 战斗结果页分析：把战斗总结、六维评分、日志送给 LLM，生成赛后教练结论。
 */
export async function generateBattleAnalysis(options: BattleAnalysisOptions): Promise<BattleAnalysisResult> {
  const apiKey = getLLMApiKey();
  if (!apiKey) {
    throw new Error("未配置 API Key。请到【设置】页保存 DeepSeek key。");
  }

  const daily = bumpDailyCount();
  if (daily.exceeded) {
    throw new Error(`今日用量超限（${daily.count}/${daily.limit}）。`);
  }

  const prompt = buildBattleAnalysisPrompt(options);
  const body = {
    model: DEEPSEEK_MODEL,
    messages: [
      { role: "system", content: BATTLE_ANALYSIS_SYSTEM_PROMPT },
      { role: "user", content: prompt },
    ],
    max_tokens: 900,
    temperature: 0.2,
    stream: false,
  };

  const res = await fetch(DEEPSEEK_API_URL, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${apiKey}`,
    },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    let rawBody = "";
    try { rawBody = await res.text(); } catch { /* ignore */ }
    throw new Error(`DeepSeek API 报错：HTTP ${res.status}${rawBody ? ` · ${rawBody}` : ""}`);
  }

  const json = await res.json();
  const content = String(json?.choices?.[0]?.message?.content ?? "");
  const parsed = extractBattleAnalysis(content);
  if (!parsed) {
    throw new Error("AI 分析结果无法解析。");
  }
  return parsed;
}

const BATTLE_ANALYSIS_SYSTEM_PROMPT = `你是一个战术赛后教练。你必须结合战斗日志、六维评分和关键事件做分析。

输出必须是纯文本，且按下面三行格式输出：
结论：...
关键点：...
建议：...

要求：
1. 结论要简短、明确。
2. 关键点必须指出 3 个左右的战术观察。
3. 建议必须给出一个最优先修改动作。
4. 不要输出 markdown，不要输出代码，不要输出额外标题。`;

function buildBattleAnalysisPrompt(options: BattleAnalysisOptions): string {
  const lines: string[] = [];
  lines.push(`[项目] ${options.projectName} (${options.projectId})`);
  lines.push(`[结果] ${options.summary.winner} · ${options.summary.reason}`);
  lines.push(`[时长] ${Math.round(options.summary.durationMs / 1000)} 秒 · ${options.summary.totalTicks} ticks`);
  lines.push(`[战绩] K/D=${options.summary.playerKills}/${options.summary.playerDeaths} · shots=${options.summary.playerShotsFired ?? 0}`);
  lines.push(`[HP] player=${options.summary.playerFinalHp ?? 0} · enemy=${options.summary.enemyFinalHp ?? 0}`);
  lines.push(`[六维]`);
  for (const dim of options.tacticalDimensions) {
    lines.push(`- ${dim.label}: ${dim.score} | ${dim.verdict} | ${dim.evidence}`);
  }
  lines.push(`[关键事件]`);
  for (const ev of options.keyEvents.slice(0, 5)) {
    lines.push(`- T${ev.tick} ${ev.side} ${ev.label}: ${ev.description}`);
  }
  lines.push(`[日志摘要]`);
  for (const log of options.logs.slice(0, 24)) {
    lines.push(`- T${log.tick} [${log.tag}] [${log.team}] ${log.message}${log.fileLine ? ` (${log.fileLine})` : ""}`);
  }
  if (options.question) {
    lines.push(`[追问] ${options.question}`);
  }
  lines.push(`请输出三行：结论 / 关键点 / 建议。`);
  return lines.join("\n");
}

function extractBattleAnalysis(content: string): BattleAnalysisResult | null {
  const overview = matchLine(content, /^结论[:：]\s*(.+)$/m);
  const keyPointText = matchLine(content, /^关键点[:：]\s*(.+)$/m);
  const nextStep = matchLine(content, /^建议[:：]\s*(.+)$/m);
  if (!overview || !keyPointText || !nextStep) return null;
  return { overview, keyPointText, nextStep };
}

function matchLine(content: string, pattern: RegExp): string | null {
  const matched = content.match(pattern);
  return matched?.[1]?.trim() ?? null;
}

/**
 * FR-024:战斗分析流式版本(屏 5 战术顾问 Chat 用)
 * - 多轮对话:把历史 user/coach 消息一起发到 messages[]
 * - 流式输出:onChunk 回调每个 token,屏 5 实时写入 chat bubble
 * - 取消支持:AbortSignal 中断
 */
export interface BattleAnalysisStreamOptions {
  projectName: string;
  projectId: string;
  summary: unknown;   // screen-5-result.BattleSummary,避免循环依赖
  tacticalDimensions: Array<{ label: string; score: number; verdict: string; evidence: string }>;
  keyEvents: Array<{ tick: number; side: string; label: string; description: string }>;
  logs: Array<{ tick: number; tag: string; team: string; message: string }>;
  /** 玩家当前追问(可选) */
  question?: string;
  /** 多轮历史:从最近 6 条开始往前推(教练+用户) */
  history?: Array<{ role: "user" | "assistant"; content: string }>;
  /** 流式回调:每 token 触发,屏 5 实时写入 chat bubble */
  onChunk?: (delta: string, accumulated: string) => void;
  signal?: AbortSignal;
}

export interface BattleAnalysisStreamResult {
  ok: boolean;
  stage?: "no_key" | "quota" | "network" | "http" | "parse";
  message?: string;
  content?: string;
  chunks?: number;
  cancelled?: boolean;
}

export async function generateBattleAnalysisStream(
  options: BattleAnalysisStreamOptions
): Promise<BattleAnalysisStreamResult> {
  const apiKey = getLLMApiKey();
  if (!apiKey) return { ok: false, stage: "no_key", message: "未配置 API Key。请到【设置】页保存 DeepSeek key。" };

  const daily = bumpDailyCount();
  if (daily.exceeded) {
    return { ok: false, stage: "quota", message: `今日用量超限（${daily.count}/${daily.limit}）。` };
  }

  // 构造 messages:system + 历史(最近 6 条) + 当前 user
  const messages: Array<{ role: "system" | "user" | "assistant"; content: string }> = [
    { role: "system", content: BATTLE_ANALYSIS_CHAT_SYSTEM_PROMPT },
  ];
  if (options.history && options.history.length > 0) {
    // 只取最近 6 条避免超 token
    for (const m of options.history.slice(-6)) {
      messages.push({ role: m.role, content: m.content });
    }
  }
  messages.push({
    role: "user",
    content: buildBattleAnalysisChatPrompt(options),
  });

  const body = {
    model: DEEPSEEK_MODEL,
    messages,
    max_tokens: 600,
    temperature: 0.3,
    stream: true,
  };

  let res: Response;
  try {
    res = await fetch(DEEPSEEK_API_URL, {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${apiKey}` },
      body: JSON.stringify(body),
      signal: options.signal,
    });
  } catch (e) {
    return { ok: false, stage: "network", message: `网络请求失败：${(e as Error).message}` };
  }
  if (!res.ok) {
    let raw = "";
    try { raw = await res.text(); } catch { /* ignore */ }
    return { ok: false, stage: "http", message: `DeepSeek API 报错：HTTP ${res.status}${raw ? ` · ${raw.slice(0, 200)}` : ""}` };
  }
  if (!res.body) return { ok: false, stage: "parse", message: "流式响应无 body" };

  const reader = res.body.getReader();
  const decoder = new TextDecoder("utf-8");
  let buffer = "";
  let accumulated = "";
  let chunks = 0;
  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      if (options.signal?.aborted) {
        reader.cancel();
        return { ok: false, stage: "parse", cancelled: true, content: accumulated, message: "已取消" };
      }
      buffer += decoder.decode(value, { stream: true });
      const events = buffer.split("\n\n");
      buffer = events.pop() || "";
      for (const evt of events) {
        const line = evt.trim();
        if (!line.startsWith("data:")) continue;
        const payload = line.slice(5).trim();
        if (payload === "[DONE]") continue;
        try {
          const parsed = JSON.parse(payload);
          const delta: string = parsed?.choices?.[0]?.delta?.content ?? "";
          if (delta) {
            accumulated += delta;
            chunks++;
            // 直接操作 DOM bubble(屏 5 调用方传入的写入函数)
            options.onChunk?.(delta, accumulated);
          }
        } catch {
          // 跳过部分 JSON
        }
      }
    }
  } catch (e) {
    return { ok: false, stage: "network", message: `流式读取失败：${(e as Error).message}`, content: accumulated };
  }
  return { ok: true, content: accumulated, chunks };
}

const BATTLE_ANALYSIS_CHAT_SYSTEM_PROMPT = `你是"CS-07"战术顾问(代号 CS,Combat Specialist 07)。请用军事化、简洁、专业的口吻与玩家交流。

【背景】玩家刚完成一局 BattleCode 坦克 1v1 战斗,你来解读他的表现。
【风格】冷峻、精准、不奉承,像特种部队教官。直接给结论,不要寒暄。
【格式】纯文本,不要 markdown 标题/列表/代码块。控制在 200 字内。
【任务】回答玩家的提问,或主动指出最关键的 1 个战术问题 + 1 个改进动作。`;

function buildBattleAnalysisChatPrompt(options: BattleAnalysisStreamOptions): string {
  // 避免循环依赖,screen-5-result.BattleSummary 形状约定为这些字段(用 any 简化)
  const summary = options.summary as {
    winner: string;
    reason: string;
    durationMs: number;
    totalTicks: number;
    playerKills: number;
    playerDeaths: number;
    playerShotsFired?: number;
    playerFinalHp?: number;
    enemyFinalHp?: number;
  };
  const lines: string[] = [];
  lines.push(`[项目] ${options.projectName} (${options.projectId})`);
  lines.push(`[结果] ${summary.winner} · ${summary.reason}`);
  lines.push(`[时长] ${Math.round(summary.durationMs / 1000)} 秒 · ${summary.totalTicks} ticks`);
  lines.push(`[战绩] K/D=${summary.playerKills}/${summary.playerDeaths} · shots=${summary.playerShotsFired ?? 0}`);
  lines.push(`[HP] player=${summary.playerFinalHp ?? 0} · enemy=${summary.enemyFinalHp ?? 0}`);
  lines.push(`[六维]`);
  for (const dim of options.tacticalDimensions) {
    lines.push(`- ${dim.label}: ${dim.score} | ${dim.verdict} | ${dim.evidence}`);
  }
  lines.push(`[关键事件]`);
  for (const ev of options.keyEvents.slice(0, 3)) {
    lines.push(`- T${ev.tick} ${ev.side} ${ev.label}: ${ev.description}`);
  }
  if (options.question) {
    lines.push(`[玩家提问] ${options.question}`);
  } else {
    lines.push(`[无提问] 请主动指出最关键的 1 个战术问题和 1 个改进动作。`);
  }
  return lines.join("\n");
}

// ============================================================
// Harness 自动化层 FR-B — Planner / Reviewer / Multi-Agent
// ============================================================

const PLANNER_SYSTEM_PROMPT = `你是 BattleCode 战术规划师。玩家用自然语言描述战术意图，你将其拆解为结构化策略。

【任务】分析玩家意图，输出策略描述和关键决策点列表。

【输出格式】
你必须严格按照以下格式输出（纯文本，不要 markdown）：

策略: <一句话概括核心战术思路，中文30字内>
关键决策:
- <决策点1>
- <决策点2>
- <决策点3>

要求：
- 策略必须具体可执行（不说"可能"，说"当X时做Y"）
- 关键决策点 3-5 个，按优先级排序
- 输出中不要引用 API 名称（那是代码生成器的工作）`;

const REVIEWER_SYSTEM_PROMPT = `你是 BattleCode 代码审查员，检查 Python tank bot 代码是否符合规范。

【检查清单（9 条硬性规则）】
1. 是否有 \`def think(self):\` 函数定义（self 参数必须）
2. 是否 import 了模块（禁止）
3. 是否用了 try/except（禁止，让错误暴露）
4. 是否用了模块级 \`_STATE = {}\` 字典（禁止，多坦克下共享状态导致行为混乱）
5. 是否有 5a 能力说明（\`# === Bot 能力说明 ===\` 4 行，紧贴 def think 上方）
6. 函数体内 if/elif/else/for/while 块开头是否有 # 注释（5b 块注释）
7. self.shoot() / self.move() / self.nearest_cover（属性，不加括号）等关键调用上方是否有 # 注释（5c 关键调用注释）
8. API 使用是否正确：enemy 是属性（self.enemy）不是方法（enemy()）；move/shoot/scan 是 self 的方法
9. V3 多坦克 API 是否正确：shoot(enemy_index) 参数是 int（敌人 tank_index）；self.enemies 是列表需遍历；self.tank_index 用于角色分工

【输出格式】
你必须严格按照以下格式输出（纯文本，不要 markdown）：

通过: <是/否>
问题:
- [<严重程度: error|warn>] <行号> <问题描述>

要求：通过=是 时问题列表为空；通过=否 时列出所有问题`;

export interface PlanResult {
  ok: true;
  strategy: string;
  keyDecisions: string[];
}

export interface ReviewResult {
  ok: true;
  passed: boolean;
  issues: Array<{
    severity: "error" | "warn";
    line?: number;
    message: string;
  }>;
}

/**
 * FR-B Planner：拆解玩家 prompt 为结构化策略描述
 */
export async function generatePlan(prompt: string): Promise<PlanResult | LLMError> {
  const apiKey = getLLMApiKey();
  if (!apiKey) return { ok: false, stage: "no_key", message: "未配置 API Key。" };

  const daily = bumpDailyCount();
  if (daily.exceeded) return { ok: false, stage: "quota", message: `今日用量超限(${daily.count}/${daily.limit})。` };

  const body = {
    model: DEEPSEEK_MODEL,
    messages: [
      { role: "system", content: PLANNER_SYSTEM_PROMPT },
      { role: "user", content: `[玩家战术意图]\n${prompt}\n\n请分析并输出策略和关键决策点。` },
    ],
    max_tokens: 600,
    temperature: 0.3,
    stream: false,
  };

  let res: Response;
  try {
    res = await fetch(DEEPSEEK_API_URL, {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${apiKey}` },
      body: JSON.stringify(body),
    });
  } catch (e) {
    return { ok: false, stage: "network", message: `网络请求失败：${(e as Error).message}` };
  }
  if (!res.ok) {
    return { ok: false, stage: "http", message: `HTTP ${res.status}` };
  }

  const json = await res.json();
  const content: string = json?.choices?.[0]?.message?.content ?? "";
  return parsePlanResponse(content);
}

function parsePlanResponse(content: string): PlanResult | LLMError {
  const strategyMatch = content.match(/策略[:：]\s*(.+)/);
  const strategy = strategyMatch?.[1]?.trim() ?? content.trim().split("\n")[0];
  const decisions = content.match(/-\s*(.+)/g)?.map((d) => d.replace(/^-\s*/, "").trim()) ?? [];
  if (!strategy) {
    return { ok: false, stage: "empty", message: "Planner 返回空策略" };
  }
  return { ok: true, strategy, keyDecisions: decisions };
}

/**
 * FR-B Reviewer：检查生成代码是否符合规范
 */
export async function reviewCode(code: string): Promise<ReviewResult | LLMError> {
  const apiKey = getLLMApiKey();
  if (!apiKey) return { ok: false, stage: "no_key", message: "未配置 API Key。" };

  const daily = bumpDailyCount();
  if (daily.exceeded) return { ok: false, stage: "quota", message: `今日用量超限(${daily.count}/${daily.limit})。` };

  const body = {
    model: DEEPSEEK_MODEL,
    messages: [
      { role: "system", content: REVIEWER_SYSTEM_PROMPT },
      { role: "user", content: `[待审查代码]\n\`\`\`python\n${code}\n\`\`\`\n\n请审查以上代码。` },
    ],
    max_tokens: 800,
    temperature: 0.1,
    stream: false,
  };

  let res: Response;
  try {
    res = await fetch(DEEPSEEK_API_URL, {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${apiKey}` },
      body: JSON.stringify(body),
    });
  } catch (e) {
    return { ok: false, stage: "network", message: `网络请求失败：${(e as Error).message}` };
  }
  if (!res.ok) {
    return { ok: false, stage: "http", message: `HTTP ${res.status}` };
  }

  const json = await res.json();
  const content: string = json?.choices?.[0]?.message?.content ?? "";
  return parseReviewResponse(content);
}

function parseReviewResponse(content: string): ReviewResult | LLMError {
  const passedMatch = content.match(/通过[:：]\s*(是|否)/);
  const passed = passedMatch?.[1] === "是";

  const issues: ReviewResult["issues"] = [];
  const issueLines = content.match(/-\s*\[(error|warn)\]\s*(\d+)?\s*(.+)/gi);
  if (issueLines) {
    for (const line of issueLines) {
      const m = line.match(/-\s*\[(error|warn)\]\s*(\d+)?\s*(.+)/i);
      if (m) {
        issues.push({
          severity: m[1].toLowerCase() as "error" | "warn",
          line: m[2] ? parseInt(m[2], 10) : undefined,
          message: m[3].trim(),
        });
      }
    }
  }

  return { ok: true, passed, issues };
}

/**
 * FR-B Multi-Agent 编排：Planner → Worker → Reviewer
 * 单次调用完成三步管道，返回最终代码。
 * 如果 Reviewer 不通过，使用 FIX_SYSTEM_PROMPT 修复一次再 Review。
 */
export async function generateCodeMultiAgent(
  prompt: string,
  options?: GenerateOptions,
): Promise<LLMResult & { plan?: PlanResult; review?: ReviewResult }> {
  // Step 1: Planner — 拆解意图
  const planResult = await generatePlan(prompt);
  if (!planResult.ok) {
    return planResult; // 透传 LLMError
  }

  // Step 2: Worker — 用增强 prompt 生成代码
  const enhancedPrompt = [
    prompt,
    "",
    `[策略指引] ${planResult.strategy}`,
    `[关键决策] ${planResult.keyDecisions.map((d) => `- ${d}`).join("\n")}`,
  ].join("\n");

  const workerResult = await generateBotCode(enhancedPrompt, options);
  if (!workerResult.ok) {
    return workerResult;
  }

  // Step 3: Reviewer — 检查代码
  const reviewResult = await reviewCode(workerResult.code);
  if (!reviewResult.ok) {
    // Reviewer 调用失败不阻塞，直接返回 Worker 生成的代码
    return { ...workerResult, plan: planResult };
  }

  if (reviewResult.passed) {
    return { ...workerResult, plan: planResult, review: reviewResult };
  }

  // Reviewer 未通过 → 尝试自动修复 1 轮
  const fixPrompt = [
    "[当前代码]",
    "```python",
    workerResult.code,
    "```",
    "",
    "[审查问题]",
    ...reviewResult.issues.map((i) => `- [${i.severity}] ${i.line ? `L${i.line} ` : ""}${i.message}`),
    "",
    "请修复以上审查问题，输出完整的修复后 Python 代码（保留代码逻辑，只修规范问题）。",
  ].join("\n");

  // 重新调用 Worker（用 fix prompt）
  const fixBody = {
    model: DEEPSEEK_MODEL,
    messages: [
      { role: "system", content: SYSTEM_PROMPT },
      { role: "user", content: fixPrompt },
    ],
    max_tokens: 4096,
    temperature: 0.2,
    stream: false,
  };

  const apiKey = getLLMApiKey();
  if (!apiKey) return { ok: false, stage: "no_key", message: "未配置 API Key。" };

  let res: Response;
  try {
    res = await fetch(DEEPSEEK_API_URL, {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${apiKey}` },
      body: JSON.stringify(fixBody),
    });
  } catch (e) {
    // fix 调用失败不阻塞，返回 Worker 代码
    return { ...workerResult, plan: planResult, review: reviewResult };
  }
  if (!res.ok) {
    return { ...workerResult, plan: planResult, review: reviewResult };
  }

  const json = await res.json();
  const fixedContent: string = json?.choices?.[0]?.message?.content ?? "";
  const fixedCode = extractCode(fixedContent);

  if (fixedCode) {
    return { ok: true, code: fixedCode, prompt, usage: json.usage, plan: planResult, review: reviewResult };
  }

  return { ...workerResult, plan: planResult, review: reviewResult };
}
